import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radio, Users, MessageSquare, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function SharePreview() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [err, setErr] = useState(null);
  const [confirmed18, setConfirmed18] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("nw_age_ok") === "yes"
  );
  useEffect(() => {
    let alive = true;
    let stopped = false;
    const load = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/rooms/${slug}/preview`);
        if (alive) setRoom(data);
      } catch (e) {
        if (alive) setErr(formatErr(e));
        stopped = true; // no point retrying a 404
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; stopped = true; clearInterval(t); };
  }, [slug]);

  const enterRoom = () => {
    if (!confirmed18) {
      localStorage.setItem("nw_age_ok", "yes");
      setConfirmed18(true);
    }
    if (user) {
      nav(`/room/${slug}`);
    } else {
      nav(`/enter?next=/room/${slug}`);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("LINK COPIED");
    } catch {
      toast.error("Copy failed. Long-press the URL bar.");
    }
  };

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div className="font-mono text-blaze text-xs tracking-widest">// ROOM NOT FOUND</div>
        <h1 className="font-heading text-6xl md:text-7xl mt-3">404.</h1>
        <p className="font-mono text-white/60 text-sm mt-4">// this room ghosted us</p>
        <button className="btn-acid mt-8" onClick={() => nav("/")} data-testid="share-back-home">GO HOME</button>
      </div>
    );
  }

  if (!room) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-acid">LOADING PREVIEW…</div>;
  }

  const latest = room.latest_at ? new Date(room.latest_at) : null;
  const timeAgo = latest ? relativeTime(latest) : "no messages yet";

  return (
    <div className="min-h-screen w-full relative overflow-hidden" data-testid="share-preview">
      <div className="min-h-screen flex flex-col">
        <header className="p-6 md:p-10 flex justify-between items-center border-b border-white/10">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-acid" />
            <span className="font-heading text-2xl leading-none">NITE<span className="text-acid">WIRE</span></span>
          </div>
          <div className="font-mono text-[10px] md:text-xs text-white/40 tracking-widest">// SHARED ROOM</div>
        </header>

        <main className="flex-1 flex flex-col items-center justify-center px-6 py-10 md:py-14">
          <div className="w-full max-w-3xl">
            <div className="font-mono text-xs tracking-widest" style={{ color: room.color }}>
              {room.preset ? "// PRESET ROOM" : `// BY @${room.created_by}`}
            </div>
            <h1 className="font-heading text-6xl md:text-8xl leading-none mt-3" data-testid="share-room-name">
              {room.name}
            </h1>
            <p className="font-body text-white/80 text-base md:text-lg mt-6 max-w-2xl">{room.topic}</p>

            <div className="grid grid-cols-3 gap-0 mt-10 border-l border-t border-white/15">
              <Stat testId="stat-live" icon={<Users className="w-4 h-4" />} label="LIVE NOW" value={room.live_count} accent />
              <Stat testId="stat-messages" icon={<MessageSquare className="w-4 h-4" />} label="MESSAGES" value={room.message_count} />
              <Stat testId="stat-latest" label="LAST HEARD" value={timeAgo} small />
            </div>

            <div className="mt-10 border border-acid/60 p-4 md:p-5 flex items-start gap-3">
              <div className="font-mono text-acid text-xs tracking-widest shrink-0">[ LIVE ]</div>
              <div className="font-mono text-white/70 text-xs md:text-sm">
                Rooms are live and moving in real time. Tap enter to walk straight into the conversation.
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="share-enter-btn" onClick={enterRoom} className="btn-acid flex items-center justify-center gap-2 flex-1">
                {user ? "JUMP IN NOW" : "ENTER"} <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="share-copy-btn" onClick={copyLink} className="btn-brutal">
                COPY LINK
              </button>
            </div>

            <div className="divider-ascii mt-12 text-xs">// // // // // // // // // // // // // // //</div>
            <p className="font-mono text-white/40 text-[10px] mt-6 tracking-widest text-center">
              NITEWIRE · ALWAYS LIVE · DIALOGUE ONLY
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent, small, testId }) {
  return (
    <div className="border-r border-b border-white/15 p-4 md:p-5" data-testid={testId}>
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/50">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`mt-2 font-heading leading-none ${small ? "text-lg md:text-2xl text-white/80 font-mono" : accent ? "text-4xl md:text-5xl text-acid" : "text-4xl md:text-5xl text-paper"}`}>
        {value}
      </div>
    </div>
  );
}

function relativeTime(d) {
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
